import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { shareGraph, shareMeta, type GraphWindow } from "@/lib/api";
import { GraphImg } from "@/components/GraphImg";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/lib/i18n";

const WINDOWS: GraphWindow[] = ["6h", "24h", "7d", "30d"];

/**
 * The customer-facing page behind a share link. No sidebar, no login, and
 * deliberately nothing but the operator's own labels: the device name, the
 * interface name and the port's capacity all stay inside the panel.
 */
export default function SharePage() {
  const { token = "" } = useParams<{ token: string }>();
  const { t } = useI18n();
  const [win, setWin] = useState<GraphWindow>("24h");
  const { data, error, isLoading } = useQuery({
    queryKey: ["share", token],
    queryFn: () => shareMeta(token),
    retry: false,
  });

  if (isLoading) {
    return <Centered>{t("share.loading")}</Centered>;
  }
  if (error || !data) {
    return <Centered>{t("share.invalid")}</Centered>;
  }

  return (
    <div className="min-h-dvh bg-slate-950 p-4 text-slate-100 md:p-8">
      <header className="mx-auto mb-6 flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold md:text-2xl">{data.label}</h1>
          <p className="text-[13px] text-slate-400">{t("share.subtitle")}</p>
        </div>
        <Tabs value={win} onValueChange={(v) => setWin(v as GraphWindow)}>
          <TabsList className="h-8">
            {WINDOWS.map((w) => (
              <TabsTrigger key={w} value={w} className="px-3 text-xs">
                {w}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </header>

      <div className="mx-auto max-w-5xl space-y-4">
        {data.graphs.map((g) => (
          <section key={g.id} className="rounded-xl border border-slate-800 bg-slate-900/40">
            <h2 className="border-b border-slate-800 px-4 py-2 text-sm text-slate-300">{g.label}</h2>
            <div className="p-3">
              <GraphImg
                queryKey={["share-graph", token, g.id, win]}
                fetch={(width) => shareGraph(token, g.id, win, width)}
                alt={g.label}
              />
            </div>
          </section>
        ))}
      </div>

      <footer className="mx-auto mt-8 max-w-5xl text-center text-xs text-slate-600">
        {t("share.footer")}
      </footer>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-950 p-6 text-center text-slate-400">
      {children}
    </div>
  );
}
